import { businessUnitTypeSchema } from "@station/contracts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AccountingPage, companyLabel, companySearchShape } from "../../../features/accounting/company.js";
import { AccountLedgerReport } from "../../../features/accounting/reports/account-ledger.js";
import { GeneralLedgerReport, JournalBookReport } from "../../../features/accounting/reports/books.js";
import type { ReportProps, ReportSearch } from "../../../features/accounting/reports/report-frame.js";
import {
  BalanceSheetReport,
  IncomeStatementReport,
  ProfitabilityReport,
} from "../../../features/accounting/reports/statements.js";
import { TrialBalanceReport } from "../../../features/accounting/reports/trial-balance.js";
import { startOfYear, todayIstanbul } from "../../../features/accounting/shared.js";

const REPORTS = [
  "trial-balance",
  "account-ledger",
  "journal-book",
  "general-ledger",
  "balance-sheet",
  "income-statement",
  "profitability",
] as const;
type ReportKey = (typeof REPORTS)[number];

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const flag = z.enum(["true"]).optional().catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(REPORTS).optional().catch(undefined),
  from: date,
  to: date,
  drafts: flag,
  level: z.coerce.number().int().min(1).max(7).optional().catch(undefined),
  hideZero: flag,
  station: z.string().uuid().optional().catch(undefined),
  unit: businessUnitTypeSchema.optional().catch(undefined),
  costCenter: z.string().uuid().optional().catch(undefined),
  account: z.string().uuid().optional().catch(undefined),
  asOf: date,
  compareAsOf: date,
  compareFrom: date,
  compareTo: date,
  groupBy: z.enum(["STATION", "BUSINESS_UNIT", "STATION_BUSINESS_UNIT"]).optional().catch(undefined),
  codeFrom: z
    .string()
    .regex(/^\d{1,3}$/)
    .optional()
    .catch(undefined),
  codeTo: z
    .string()
    .regex(/^\d{1,3}$/)
    .optional()
    .catch(undefined),
  page: z.coerce.number().int().min(0).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/muhasebe/raporlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ReportsPage,
});

const COMPONENTS: Record<ReportKey, (props: ReportProps) => ReactElement> = {
  "trial-balance": TrialBalanceReport,
  "account-ledger": AccountLedgerReport,
  "journal-book": JournalBookReport,
  "general-ledger": GeneralLedgerReport,
  "balance-sheet": BalanceSheetReport,
  "income-statement": IncomeStatementReport,
  profitability: ProfitabilityReport,
};

function ReportsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const tab = search.tab ?? "trial-balance";
  // Varsayılan aralık: içinde bulunulan yılın başından bugüne (raporlar mali yıl bazındadır).
  const today = todayIstanbul();
  const range = { from: search.from ?? startOfYear(search.to ?? today), to: search.to ?? today };
  const setSearch = (patch: Partial<ReportSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(
          Object.entries({ ...previous, ...patch }).filter(
            ([, value]) => value !== undefined && value !== "",
          ),
        ),
      replace: true,
    });

  return (
    <AccountingPage title={t("menu.accountingReports")} description={t("accounting.reports.description")}>
      {({ companyId, company }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({
              // Sekme değişince ortak süzgeçler (tarih, taslak, şirket) korunur; rapora özgü olanlar atılır.
              search: (previous) => ({
                company: previous.company,
                from: previous.from,
                to: previous.to,
                drafts: previous.drafts,
                tab: next as ReportKey,
              }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("menu.accountingReports")} className="flex-wrap print:hidden">
            {REPORTS.map((report) => (
              <TabsTrigger key={report} value={report}>
                {t(`accounting.reports.tabs.${report}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {REPORTS.map((report) => {
            const Component = COMPONENTS[report];
            return (
              <TabsContent key={report} value={report}>
                {tab === report ? (
                  <Component
                    companyId={companyId}
                    companyName={`${company.legalName}${company.shortName ? ` (${companyLabel(company)})` : ""}`}
                    search={search}
                    setSearch={setSearch}
                    range={range}
                  />
                ) : null}
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </AccountingPage>
  );
}
